import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import { Botao, EmptyState, ErrorState, PageHeader, Skeleton } from '../../../ui';
import DocumentoVistoria from '../../../components/vistoria/DocumentoVistoria';

import {
  getUltimaVistoriaPorVeiculo,
  UltimaVistoriaPorVeiculoResponse,
} from '../../../api/vistoria/vistoriaApi';
import { normalizarUrlImagem, ordenarAvariasPrimeiro } from '../../../utils/vistoriaImagens';
import { VISTORIA } from '../routes';

/**
 * `/vistoria/veiculo/:vehicleId/laudo` — a **última vistoria de um veículo**,
 * somente leitura (reescrita na at-64, frente D/D2).
 *
 * É o "Ver laudo" do painel e o "Ver vistoria" da carteira. **Não faz parte do
 * fluxo** (não tem `Stepper`) e **não é a tela de finalizar** — essa é
 * `/vistoria/laudo`.
 *
 * ## O que mudou
 *
 *   1. **O documento é o `DocumentoVistoria` compartilhado** — as seções eram uma
 *      terceira cópia das mesmas oito.
 *   2. **404 e falha deixaram de ser toast.** "Nenhuma vistoria encontrada" virou
 *      `EmptyState` (era um bloco com `style` inline) e falha de rede virou
 *      `ErrorState` com "Tentar novamente" (era `toast.error` + tela em branco).
 *   3. **`Skeleton` no lugar do `FullScreenLoader`.**
 *   4. 🐛 **A data sai em `pt-BR`.** Era `toLocaleString()` **sem locale**, então o
 *      formato mudava conforme o navegador — a única das telas do fluxo que fazia
 *      isso.
 *   5. **`normalizeImageUrl` saiu daqui** para `utils/vistoriaImagens.ts` (era uma
 *      das duas cópias, cada uma com o nome do bucket escrito à mão).
 *
 * ⚠️ **O link "Visualizar" por foto fica**, agora como ação da miniatura: é o que
 * permite abrir a imagem em tamanho real, e num laudo de 29 fotos isso importa.
 */
const VistoriaLaudoVeiculoPage: React.FC = () => {
  const navigate = useNavigate();
  const voltar = useVoltar(VISTORIA());
  const { vehicleId } = useParams();

  const [dados, setDados] = React.useState<UltimaVistoriaPorVeiculoResponse | null>(null);
  const [carregando, setCarregando] = React.useState(Boolean(vehicleId));
  /** 404 e falha de verdade são coisas diferentes — ver o item 2 do cabeçalho. */
  const [naoEncontrada, setNaoEncontrada] = React.useState(false);
  const [erro, setErro] = React.useState<string | null>(null);

  const buscar = React.useCallback(async () => {
    if (!vehicleId) return;

    setCarregando(true);
    setErro(null);
    setNaoEncontrada(false);

    try {
      const resposta = await getUltimaVistoriaPorVeiculo({ vehicleId });
      setDados(resposta);
    } catch (falha: any) {
      setDados(null);
      if (falha?.response?.status === 404) {
        setNaoEncontrada(true);
      } else {
        setErro(falha?.message || 'Falha ao buscar a vistoria deste veículo.');
      }
    } finally {
      setCarregando(false);
    }
  }, [vehicleId]);

  React.useEffect(() => {
    void buscar();
  }, [buscar]);

  const vistoria = dados?.vistoria;

  const fotos = React.useMemo(
    () =>
      ordenarAvariasPrimeiro(vistoria?.imagens ?? []).map((foto) => {
        const url = normalizarUrlImagem(foto.url) || foto.url;

        return {
          url,
          legenda: foto.legenda,
          avaria: foto.damaged === true,
          acoes: (
            <Botao
              variante="fantasma"
              tamanho="sm"
              larguraTotal
              onClick={() => window.open(url, '_blank', 'noreferrer')}
            >
              Visualizar
            </Botao>
          ),
        };
      }),
    [vistoria],
  );

  return (
    <ShellSaac
      cabecalho={<PageHeader sobretitulo="Vistoria" titulo="Laudo de vistoria" aoVoltar={voltar} />}
    >
      <div className="tw-min-h-full tw-bg-shell">
        <div className="tw-mx-auto tw-w-full tw-max-w-3xl md:tw-p-6">
          <div className="tw-bg-white tw-p-4 md:tw-rounded-2xl md:tw-border md:tw-border-superficie-borda md:tw-p-6 md:tw-shadow-[0_2px_12px_rgba(0,0,0,0.09)]">
            {carregando ? (
              <Skeleton linhas={6} altura="tw-h-14" />
            ) : erro ? (
              <ErrorState
                mensagem="Não foi possível carregar o laudo."
                detalhe={erro}
                aoTentarNovamente={() => void buscar()}
              />
            ) : naoEncontrada || !vistoria ? (
              <EmptyState
                titulo="Nenhuma vistoria encontrada para este veículo"
                descricao="Este veículo ainda não tem vistoria concluída. Inicie uma pelo painel de vistorias."
                icone="sem-dados"
                acao={
                  <Botao variante="primario" onClick={() => navigate(VISTORIA())}>
                    Ir para o painel
                  </Botao>
                }
              />
            ) : (
              <DocumentoVistoria
                associado={{
                  nome: vistoria.associado?.nomeCompleto,
                  cpf: vistoria.associado?.cpfCnpj,
                  email: vistoria.associado?.email,
                }}
                veiculo={{
                  placa: vistoria.veiculo?.placa,
                  modelo: vistoria.veiculo?.modelo,
                  anoModelo: vistoria.veiculo?.anoModelo,
                  anoFabricacao: vistoria.veiculo?.anoFabricacao,
                }}
                /* A API já devolve o checklist agrupado em rótulos — é o mesmo
                   shape que `agruparChecklist` produz para as outras duas telas. */
                checklist={{
                  bom: vistoria.checklist?.bom ?? [],
                  ruim: vistoria.checklist?.ruim ?? [],
                  naoExistente: vistoria.checklist?.naoExistente ?? [],
                }}
                observacao={vistoria.checklist?.observacao}
                fotos={fotos}
                assinaturaUrl={normalizarUrlImagem(vistoria.assinaturaUrl)}
                pdfUrl={vistoria.pdf_image_url ? String(vistoria.pdf_image_url) : null}
                dataVistoria={
                  vistoria.createdAt
                    ? new Date(vistoria.createdAt).toLocaleString('pt-BR')
                    : undefined
                }
                secoes={{ vistoria: true }}
              />
            )}
          </div>
        </div>
      </div>
    </ShellSaac>
  );
};

export default VistoriaLaudoVeiculoPage;
